/**
 * Accepted Reentry Plan — persists the user's commitment to a return-to-
 * training plan and tracks ramp progression.
 *
 * The reentry detection (lib/reentryState.ts) classifies the user every
 * page load. The plan (lib/returnToTrainingPlan.ts) recommends a cap +
 * ramp + rewind. Until now, those were advisory only — the user could
 * read them but the system never *committed* to a structured ramp.
 *
 * When the user accepts:
 *   1. We snapshot the plan to users/{uid}.acceptedReentryPlan
 *   2. We optionally rewind the program by `programRewindDays`
 *   3. The workout generator now prefers the *accepted* plan's ramp-week
 *      intensity over the auto-detected per-render cap. The accepted plan
 *      relaxes the cap over time (week 1 → ... → full intensity).
 *   4. When all ramp weeks have elapsed OR the user explicitly ends it,
 *      the plan auto-clears and normal training resumes.
 */

import { doc, getDoc, setDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import { db as firestoreDb } from './firebase';
import { saveUserProgramSelection, getUserProgramSelection } from './programManagement';
import type { ReturnPlan } from './returnToTrainingPlan';

export interface AcceptedReentryPlan {
  /** ISO timestamp when the user accepted. */
  acceptedAt: string;
  /** Days off at the moment of acceptance — frozen for the duration of the ramp. */
  daysOffAtAccept: number;
  /** Estimated fitness retained at acceptance (0-100). */
  fitnessRetained: number;
  /** Week-1 intensity cap (% of pre-break strength load). */
  week1IntensityPct: number;
  /** Week-1 aerobic intensity cap (% of pre-break pace/volume). */
  week1AerobicIntensityPct?: number;
  /** Total ramp duration in weeks (after age modifier). */
  rampWeeks: number;
  /** Age multiplier that was applied to ramp duration (1.0, 1.2, 1.5). */
  ageMultiplier?: number;
  /** How the user chose to ramp — affects whether program rewind runs. */
  mode?: 'program' | 'freestyle' | 'reconditioning';
  /** How many program days we rewound on acceptance (0 means no rewind happened). */
  programRewindDays: number;
  /** Snapshot of where the program was before rewind — for diagnostic / undo. */
  preRewindWeek?: number;
  preRewindDay?: number;
  /**
   * Sessions completed under the ramp. Progression is session-count based —
   * NOT calendar-day based — so a 1-week ramp can't "complete" after a single
   * workout. Each ramp week requires SESSIONS_PER_RAMP_WEEK completed sessions
   * before the cap relaxes to the next tier.
   */
  rampSessionsCompleted?: number;
  /**
   * True after the reentry pipeline has already applied the Bosquet 1RM
   * decay to the user's stored e1RMs. Gate flag — without it, every
   * session render would re-decay the already-decayed numbers and the
   * baseline would spiral down. Set to true exactly once when the plan
   * is auto-accepted or manually accepted.
   */
  e1RMsDecayed?: boolean;
  /** Retention % applied to e1RMs at decay time (for diagnostic + UI). */
  e1RMRetentionPct?: number;
}

/**
 * How many sessions must complete inside a ramp week before the cap relaxes
 * to the next week. 3 is the floor for a real adaptation block — anything
 * less and we're calling a single workout a "week" again.
 */
export const SESSIONS_PER_RAMP_WEEK = 3;

/**
 * Programs designed PURELY as return-to-training ramps — i.e. their own
 * progression IS the reentry plan. Layering an intensity cap on top
 * double-caps the user, producing comically light sessions.
 *
 * 12-week programs (recruit / first-mile / foundations) are NOT ramps —
 * they're full training blocks. A "Foundations" name is misleading here:
 * those programs build base fitness, not return-from-detraining. The
 * cap should still apply.
 *
 * If a new dedicated ramp-up program is added (e.g. ramp-up-beginner,
 * ramp-up-intermediate), register its ID here.
 */
export const REENTRY_FRIENDLY_PROGRAMS: ReadonlySet<string> = new Set([
  'recruit-ramp-up-1week',   // beginner / heavy detraining (1 wk, 4 sessions)
  'operator-ramp-up-2week',  // intermediate (2 wk, 10 sessions)
  'operator-reset-3week',    // advanced / 60+ days off (3 wk, 13 sessions)
]);

/**
 * Pick the most appropriate ramp-up program for a returning user, based on
 * how long they've been off and (optionally) their experience level.
 *
 * Rules of thumb (literature-aligned):
 *   - 60+ days off (>60 days): use the 3-week reset regardless of level —
 *     connective tissue + aerobic base need real time
 *   - 14-60 days off, advanced/intermediate: standard 2-week ramp
 *   - 14-60 days off, beginner: 1-week recruit ramp keeps the bar low
 *   - <14 days off: no formal ramp needed (caller should fall back to
 *     in-program reentry cap, not a new program)
 */
export function pickRampProgram(
  daysOff: number,
  experienceLevel?: 'beginner' | 'intermediate' | 'advanced' | null,
): { programId: string; name: string; rationale: string } | null {
  if (daysOff < 14) return null;

  if (daysOff >= 60) {
    return {
      programId: 'operator-reset-3week',
      name: 'OPERATOR RESET: 3-Week Reconditioning',
      rationale: `${daysOff} days off — 3-week reconditioning rebuilds connective tissue and aerobic base before loading.`,
    };
  }

  if (experienceLevel === 'beginner') {
    return {
      programId: 'recruit-ramp-up-1week',
      name: 'RECRUIT RAMP-UP: 1-Week Re-Entry',
      rationale: `${daysOff} days off — 1-week bodyweight-first ramp to restore movement quality.`,
    };
  }

  return {
    programId: 'operator-ramp-up-2week',
    name: 'OPERATOR RAMP-UP: 2-Week Re-Entry',
    rationale: `${daysOff} days off — 2-week ramp to operator-level loading. Week 1 movement + Z2, week 2 strength signal.`,
  };
}

export function isReentryFriendlyProgram(programId: string | null | undefined): boolean {
  if (!programId) return false;
  return REENTRY_FRIENDLY_PROGRAMS.has(programId);
}

/**
 * Compute the current ramp week (1-indexed) — session-count based, NOT
 * calendar-day based. Previously a 1-week ramp could "complete" the moment
 * a user did their first workout (calendar elapsed → next week). That's not
 * a ramp. Now: a ramp week requires SESSIONS_PER_RAMP_WEEK completed sessions
 * before the cap relaxes to the next week.
 *
 * The calendar acts as a SAFETY FLOOR: if a user trains slowly (e.g. 1
 * session per week), they still progress through the ramp at the rate of
 * 1 calendar week per ramp week. They just can't shortcut by stacking
 * sessions on consecutive days.
 *
 *   Effective week = max(byCalendar, bySessions)
 *
 * Where:
 *   byCalendar = floor(elapsedDays / 7) + 1
 *   bySessions = floor(rampSessionsCompleted / SESSIONS_PER_RAMP_WEEK) + 1
 *
 * Returns 0 when the user has BOTH completed enough sessions AND enough
 * calendar weeks to graduate past the final ramp week.
 */
export function rampWeekOf(plan: AcceptedReentryPlan, now: Date = new Date()): number {
  const accepted = new Date(plan.acceptedAt);
  if (Number.isNaN(accepted.getTime())) return 1;

  const elapsedDays = Math.max(0, Math.floor((now.getTime() - accepted.getTime()) / 86_400_000));
  const byCalendar = Math.floor(elapsedDays / 7) + 1;

  const sessionsDone = plan.rampSessionsCompleted ?? 0;
  const bySessions = Math.floor(sessionsDone / SESSIONS_PER_RAMP_WEEK) + 1;

  // Effective week is the SLOWER of the two — calendar OR sessions. This
  // prevents stacking 3 sessions in 24 hours from collapsing the ramp.
  const effectiveWeek = Math.min(byCalendar, bySessions);

  if (effectiveWeek > plan.rampWeeks) return 0; // ramp complete
  return effectiveWeek;
}

/**
 * Increment the session counter on the accepted plan. Called once per
 * completed session while a plan is active. Returns the updated plan;
 * caller is responsible for persisting it.
 */
export function bumpRampSessions(plan: AcceptedReentryPlan): AcceptedReentryPlan {
  return {
    ...plan,
    rampSessionsCompleted: (plan.rampSessionsCompleted ?? 0) + 1,
  };
}

/**
 * Persist a bumped session count to Firestore. Best-effort — if the write
 * fails the next session will try again. Should be called once per completed
 * session while an accepted plan is active.
 */
export async function recordRampSession(userId: string): Promise<void> {
  try {
    const plan = await loadAcceptedReentryPlan(userId);
    if (!plan) return;
    const updated = bumpRampSessions(plan);
    await setDoc(
      doc(firestoreDb, 'users', userId),
      { acceptedReentryPlan: updated },
      { merge: true },
    );
  } catch (err) {
    console.warn('recordRampSession failed:', err);
  }
}

/**
 * Linear interpolation between week 1 cap and 100% across the ramp.
 * Returns the intensity cap factor (0-1) for the current ramp week.
 *
 *   Example: week1=60%, rampWeeks=3:
 *     Week 1 → 0.60
 *     Week 2 → 0.80
 *     Week 3 → 0.95   (final ramp week — never quite 100, that's "post-ramp")
 *     Week 4+ → null  (ramp complete)
 */
export function intensityFactorFor(plan: AcceptedReentryPlan, now: Date = new Date()): number | null {
  const week = rampWeekOf(plan, now);
  if (week === 0) return null;
  const start = plan.week1IntensityPct / 100;
  if (plan.rampWeeks <= 1) return start;
  const step = (1.0 - start) / plan.rampWeeks;
  return Math.min(1.0, start + step * (week - 1));
}

/**
 * Aerobic intensity factor for the current ramp week. Always trails the
 * strength factor (VO2 detrains ~2× faster than 1RM). Returns null when
 * the ramp is complete.
 */
export function aerobicFactorFor(plan: AcceptedReentryPlan, now: Date = new Date()): number | null {
  const week = rampWeekOf(plan, now);
  if (week === 0) return null;
  // Prefer the stored aerobic cap; fall back to strength × 0.85 if absent
  // (older accepted plans pre-date the field).
  const startAerobic = (plan.week1AerobicIntensityPct ?? Math.max(45, plan.week1IntensityPct * 0.85)) / 100;
  if (plan.rampWeeks <= 1) return startAerobic;
  const step = (1.0 - startAerobic) / plan.rampWeeks;
  return Math.min(1.0, startAerobic + step * (week - 1));
}

export type ReentryMode = 'program' | 'freestyle' | 'reconditioning';

interface AcceptOptions {
  /**
   * Which surface the user chose to ramp through. Determines whether the
   * program rewind runs (only 'program' mode rewinds).
   */
  mode?: ReentryMode;
}

/**
 * Accept a return plan. Persists it under users/{uid}.acceptedReentryPlan
 * and applies the program rewind if mode === 'program'.
 */
export async function acceptReentryPlan(
  userId: string,
  plan: ReturnPlan,
  daysOffAtAccept: number,
  options: AcceptOptions = {},
): Promise<AcceptedReentryPlan> {
  const mode = options.mode ?? 'program';
  const accepted: AcceptedReentryPlan = {
    acceptedAt: new Date().toISOString(),
    daysOffAtAccept,
    fitnessRetained: plan.fitnessRetained,
    week1IntensityPct: plan.week1IntensityPct,
    week1AerobicIntensityPct: plan.week1AerobicIntensityPct,
    rampWeeks: plan.rampWeeks,
    ageMultiplier: plan.ageMultiplier,
    mode,
    programRewindDays: 0, // updated below if rewind happens
  };

  // Program rewind only runs when mode === 'program'. Freestyle and
  // reconditioning paths don't touch the program day pointer.
  //
  // OLD behavior (subtract programRewindDays from current day pointer) was
  // wrong — it landed users at an arbitrary mid-microcycle point. The whole
  // point of a rewind is to land at the TOP of a real training week, not
  // 7 days backwards from wherever they happened to be.
  //
  // NEW behavior: rewind to W2 D1 — the first true training week in most
  // OPMODE programs (W1 is typically assessment / max-test / calibration,
  // so resuming there forces the user to redo testing instead of training).
  // If the user was already at or before W2 D1, no rewind happens — there's
  // nothing useful to roll back to.
  if (mode === 'program' && plan.programRewindDays > 0) {
    try {
      const selection = await getUserProgramSelection(userId);
      if (selection) {
        const REENTRY_RESUME_WEEK = 2;
        const REENTRY_RESUME_DAY = 1;
        const currentTotalDay = (selection.currentWeek - 1) * 7 + selection.currentDay;
        const targetTotalDay = (REENTRY_RESUME_WEEK - 1) * 7 + REENTRY_RESUME_DAY;

        if (currentTotalDay > targetTotalDay) {
          accepted.preRewindWeek = selection.currentWeek;
          accepted.preRewindDay = selection.currentDay;

          await saveUserProgramSelection(userId, {
            ...selection,
            currentWeek: REENTRY_RESUME_WEEK,
            currentDay: REENTRY_RESUME_DAY,
            skippedDays: [],
          });
          accepted.programRewindDays = currentTotalDay - targetTotalDay;
        }
      }
    } catch (err) {
      console.warn('Reentry rewind failed — plan still accepted, rewind skipped:', err);
    }
  }

  // Persist the accepted plan
  await setDoc(
    doc(firestoreDb, 'users', userId),
    {
      acceptedReentryPlan: accepted,
      acceptedReentryPlanAt: serverTimestamp(),
    },
    { merge: true },
  );

  return accepted;
}

/**
 * Load the currently-accepted plan (if any) and check whether it's still
 * within its ramp window. Returns null when the ramp has elapsed (auto-
 * cleanup left to a separate call).
 */
export async function loadAcceptedReentryPlan(userId: string): Promise<AcceptedReentryPlan | null> {
  try {
    const snap = await getDoc(doc(firestoreDb, 'users', userId));
    if (!snap.exists()) return null;
    const data = snap.data();
    const plan = data?.acceptedReentryPlan as AcceptedReentryPlan | undefined;
    if (!plan?.acceptedAt) return null;
    return plan;
  } catch {
    return null;
  }
}

/**
 * Clear the accepted plan — used when the ramp completes naturally OR
 * when the user explicitly dismisses it (e.g., feels strong sooner).
 */
export async function clearAcceptedReentryPlan(userId: string): Promise<void> {
  await setDoc(
    doc(firestoreDb, 'users', userId),
    { acceptedReentryPlan: deleteField() },
    { merge: true },
  );
}
